import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Table, Badge, Form, InputGroup, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEnvelope } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import EmailEmployeesModal from './EmailEmployeesModal';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  
  const { token } = useContext(AuthContext);
  
  // Fetch employees when component mounts
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await fetch('/api/employees?noPagination=true', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch employees');
        }
        
        const data = await response.json();
        setEmployees(data);
        setFilteredEmployees(data.filter(e => e.IsActive || showInactive));
      } catch (err) {
        console.error('Error fetching employees:', err);
        toast.error('Failed to load employees');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEmployees();
  }, [token]);
  
  // Filter employees based on search term and active status
  useEffect(() => {
    if (employees.length > 0) {
      let filtered = employees;
      
      // Filter by active status
      if (!showInactive) {
        filtered = filtered.filter(employee => employee.IsActive);
      }
      
      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(
          employee => 
            employee.FirstName.toLowerCase().includes(term) ||
            employee.LastName.toLowerCase().includes(term) ||
            (employee.Email && employee.Email.toLowerCase().includes(term))
        );
      }
      
      setFilteredEmployees(filtered);
      
      // Reset selected employees when filters change
      setSelectedEmployees([]);
    }
  }, [searchTerm, showInactive, employees]);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const toggleShowInactive = () => {
    setShowInactive(!showInactive);
  };
  
  // Format currency to display as $X,XXX.XX with commas
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };
  
  // Handle employee selection
  const handleSelectEmployee = (employee) => {
    // Check if employee is already selected
    const isSelected = selectedEmployees.some(e => e.EmployeeId === employee.EmployeeId);
    
    if (isSelected) {
      // Remove from selection
      setSelectedEmployees(selectedEmployees.filter(e => e.EmployeeId !== employee.EmployeeId));
    } else {
      // Add to selection
      setSelectedEmployees([...selectedEmployees, employee]);
    }
  };
  
  // Handle "Select All" checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all filtered employees with emails
      setSelectedEmployees(filteredEmployees.filter(e => e.Email));
    } else {
      // Deselect all
      setSelectedEmployees([]);
    }
  };
  
  // Open email modal
  const handleOpenEmailModal = () => {
    if (selectedEmployees.length === 0) {
      toast.warning('Please select at least one employee to email');
      return;
    }
    
    // Check if all selected employees have email addresses
    const missingEmails = selectedEmployees.filter(e => !e.Email);
    if (missingEmails.length > 0) {
      const names = missingEmails.map(e => `${e.FirstName} ${e.LastName}`).join(', ');
      toast.warning(`Cannot send email. Missing email addresses for: ${names}`);
      return;
    }
    
    setShowEmailModal(true);
  };
  
  // Close email modal
  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading employees...</div>;
  }
  
  // Count employees with email addresses
  const employeesWithEmail = filteredEmployees.filter(e => e.Email).length;
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Employees</h2>
        <div>
          <Button
            variant="info"
            className="me-2"
            onClick={handleOpenEmailModal}
            disabled={selectedEmployees.length === 0}
          >
            <FaEnvelope className="me-1" />
            Email Selected ({selectedEmployees.length})
          </Button>
          <Button as={Link} to="/employees/create" variant="primary">
            Add Employee
          </Button>
        </div>
      </div>
      
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={7} className="mb-3 mb-md-0">
              <InputGroup>
                <Form.Control
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Check 
                type="switch"
                id="showInactive"
                label="Show Inactive"
                checked={showInactive}
                onChange={toggleShowInactive}
              />
            </Col>
            <Col md={2}>
              <Form.Check 
                type="checkbox"
                id="selectAll"
                label={`Select All (${employeesWithEmail})`}
                onChange={handleSelectAll}
                checked={selectedEmployees.length === employeesWithEmail && employeesWithEmail > 0}
                disabled={employeesWithEmail === 0}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          {filteredEmployees.length > 0 ? (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th width="40">Select</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Hourly Rate</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map(employee => (
                    <tr key={employee.EmployeeId}>
                      <td className="text-center">
                        <Form.Check 
                          type="checkbox"
                          id={`select-${employee.EmployeeId}`}
                          checked={selectedEmployees.some(e => e.EmployeeId === employee.EmployeeId)}
                          onChange={() => handleSelectEmployee(employee)}
                          disabled={!employee.Email}
                        />
                      </td>
                      <td>
                        <div>{`${employee.FirstName} ${employee.LastName}`}</div>
                      </td>
                      <td>
                        <div>{employee.Email || <span className="text-muted">No email</span>}</div>
                        <div>{employee.Phone || <span className="text-muted">No phone</span>}</div>
                      </td>
                      <td>{formatCurrency(employee.HourlyWage)}</td>
                      <td>
                        <Badge bg={employee.IsActive ? 'success' : 'secondary'}>
                          {employee.IsActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          as={Link}
                          to={`/employees/${employee.EmployeeId}`}
                          variant="primary"
                          size="sm"
                          className="me-1"
                        >
                          View
                        </Button>
                        <Button
                          as={Link}
                          to={`/employees/${employee.EmployeeId}/edit`}
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                        >
                          Edit
                        </Button>
                        {employee.Email && (
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => {
                              setSelectedEmployees([employee]);
                              setShowEmailModal(true);
                            }}
                          >
                            <FaEnvelope className="me-1" />
                            Email
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4">
              <p>No employees found. {searchTerm && 'Try a different search term.'}</p>
              <Button as={Link} to="/employees/create" variant="primary">
                Add Employee
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
      
      {/* Email Modal */}
      <EmailEmployeesModal
        show={showEmailModal}
        handleClose={handleCloseEmailModal}
        employees={selectedEmployees}
      />
    </div>
  );
};

export default Employees;